import React from 'react'

import type { Theme, WithStyles, WithTheme } from '@material-ui/core'
import {
  withStyles,
  withTheme,
  createStyles,
  Popover,
  CardContent,
  useMediaQuery,
  CardActions
} from '@material-ui/core'
import type { PopoverProps as MuiPopoverProps } from '@material-ui/core/Popover'
import Dialog from '../Dialog'
import IconButton from '../IconButton'
import CloseIcon from '@material-ui/icons/Close'
import type { BreakpointValues } from '@material-ui/core/styles/createBreakpoints'

export interface ResponsivePopoverProps extends WithStyles<typeof styles>, WithTheme {
  open: boolean
  children: React.ReactNode
  PopoverProps?: Partial<MuiPopoverProps>
  modalBreakpoint?: keyof BreakpointValues
  footer?: React.ReactNode
  onClose?: React.ReactEventHandler<unknown>
}

const styles = (theme: Theme) =>
  createStyles({
    popover: {
      overflow: 'visible'
    },
    modal: {},
    header: {
      display: 'flex',
      justifyContent: 'flex-end',
      position: 'sticky',
      top: 0,
      background: 'white',
      zIndex: 1
    },
    modalContent: {
      padding: '16px !important',
      display: 'flex',
      flexDirection: 'column'
    },
    content: {
      flexGrow: 1,
      overflow: 'visible'
    },
    footer: {
      position: 'sticky',
      background: 'white',
      padding: theme.spacing(),
      paddingBottom: theme.spacing(2),
      zIndex: 1,
      bottom: 0
    }
  })

/**
 * A popup that will expand into a fullscreen modal on xs sized screens
 */
function ResponsivePopover({
  theme,
  classes,
  children,
  open,
  PopoverProps = {},
  onClose,
  footer,
  modalBreakpoint = 'xs'
}: ResponsivePopoverProps) {
  const isMobile = useMediaQuery(theme.breakpoints.down(modalBreakpoint))

  if (!isMobile) {
    return (
      <Popover classes={{ paper: classes.popover }} open={open} onClose={onClose} {...PopoverProps}>
        <CardContent>
          <div className={classes.content}>{children}</div>
        </CardContent>
        <CardActions className={classes.footer}>{footer}</CardActions>
      </Popover>
    )
  } else {
    return (
      <Dialog fullScreen open={open} classes={{ root: classes.modal }} onClose={onClose}>
        <div className={classes.header}>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <CardContent className={classes.modalContent}>
          <div className={classes.content}>{children}</div>
        </CardContent>
        <CardActions className={classes.footer}>{footer}</CardActions>
      </Dialog>
    )
  }
}

export default withStyles(styles)(withTheme(ResponsivePopover))
